/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEvent} from 'react';
import Button from '@material-ui/core/Button';
import {makeStyles} from '@material-ui/core/styles';
import ChevronRight from '@material-ui/icons/ChevronRight';
import {getValidElementId} from '@ion/core/src/utils/StringUtil';
import {dlsAlt, dlsColor} from '../../theme/theme';

export interface MobileMenuButtonProps {
  label: string | JSX.Element;
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
  elementId?: string;
  disabled?: boolean;
  includeChevron?: boolean;
  selectFieldValue?: string;
}

const useStyles = makeStyles(theme => ({
  root : {
    width          : '100%',
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'space-between',
    borderBottom   : `solid 1px ${dlsColor('grey5')}`
  },
  label : {
    ...dlsAlt('typography', 'label1'),
    color : dlsColor('grey3')
  },
  selectFieldValue : {
    color   : dlsColor('grey4'),
    padding : '0px 10px 0px 5px'
  }
}));

/**
 * Common mobile menu button that is full width of its container
 * and contains a chevron to the far right of it
 *
 * ```typescript
 *
 * import {MobileMenuButton} from '@ion/dls/src/components/buttons/MobileMenuButton';
 *
 * <MobileMenuButton label='Click me'
 *   onClick={onClick} />
 *
 * ```
 *
 */
export const MobileMenuButton = ({
  label,
  onClick,
  elementId,
  selectFieldValue,
  disabled = false,
  includeChevron = false
}: MobileMenuButtonProps) => {
  const classes = useStyles();
  return (
    <Button id={getValidElementId(elementId)}
      disableRipple
      disableFocusRipple
      variant='text'
      onClick={onClick}
      disabled={disabled}
      classes={{
        root  : classes.root,
        label : classes.label
      }}>
      <span>
        {label}
        {selectFieldValue && <span className={classes.selectFieldValue}>({selectFieldValue})</span>}
      </span>
      {includeChevron && <ChevronRight />}
    </Button>
  );
};

export default MobileMenuButton;
